import React from "react";
import classNames from "classnames";
import { Menu, MenuButton, MenuItem, MenuItems, MenuProps } from "@headlessui/react";
import ChevronArrowIcon from "@/component/icon/ChevronArrowIcon";
import InViewObserver from "@/component/InViewObserver";


interface DropdownGroupProps extends MenuProps {
    width?: string | number;
    className?: string;
}

const DropdownGroup: React.FC<DropdownGroupProps> = (props) => {
    const { width, children, className: propsClassName } = props;

    return (
        <Menu as="div"
              className={classNames("relative flex-none", propsClassName)}
              style={{ width: width || "100%" }}
              role="group"
              aria-label="dropdown"
        >
            {children}
        </Menu>
    );
};

interface DropdownButtonProps {
    open?: boolean;
    close?: () => void;
    as?: "button" | "div";
    variant?: "secondary";
    invalid?: boolean;
    isSelected?: boolean;
    className?: string;
    iconClassName?: string;
    btnTextRef?: React.RefObject<HTMLDivElement>;
    children: React.ReactNode;
}

export const DropdownButton: React.FC<DropdownButtonProps> = (props) => {
    const {
        open,
        close,
        as = "button",
        variant = "primary",
        invalid,
        isSelected = true,
        className: propsClassName,
        iconClassName,
        children
    } = props;

    return (
        <InViewObserver enabled={open}
                        onHidden={close}
        >{inViewRef => (
            <MenuButton as={as}
                        data-testid="dropdown-button"
                        ref={inViewRef}
                        className={classNames(
                            "dropdown-button",
                            !isSelected && "dropdown-button-placeholder",
                            `dropdown-${variant}`,
                            invalid && "input-invalid",
                            propsClassName
                        )}
            >{({ open }) => (<>
                <div className="w-full text-start truncate">
                    {children}
                </div>
                <ChevronArrowIcon direction={open ? "up" : "down"}
                                  className={classNames("flex-none size-4.5", iconClassName)}
                />
            </>)}</MenuButton>
        )}</InViewObserver>
    );
};

interface DropdownItemsProps {
    variant?: "secondary";
    className?: string;
    children: React.ReactNode;
}

export const DropdownItems: React.FC<DropdownItemsProps> = (props) => {
    const { children, variant, className: propsClassName } = props;

    return (
        <MenuItems transition
                   anchor="bottom"
                   className={classNames(
                       "dropdown-items-box w-[var(--button-width)]",
                       propsClassName,
                       variant && `dropdown-${variant}`
                   )}
        >
            {children}
        </MenuItems>
    );
};

interface DropdownItemProps {
    isOdd?: boolean;
    className?: string;
    onClick: (item: any) => void;
    children: React.ReactNode;
}

export const DropdownItem: React.FC<DropdownItemProps> = (props) => {
    const { isOdd, children, onClick, className: propsClassName } = props;

    return (
        <MenuItem as='button'
                  className={classNames("dropdown-item truncate", isOdd && "odd", propsClassName)}
                  onClick={onClick}
        >
            {children}
        </MenuItem>
    );
};

interface DropdownFooterProps {
    className?: string;
    onClick: () => void;
    children: React.ReactNode;
}

export const DropdownFooter: React.FC<DropdownFooterProps> = (props) => {
    const { onClick, children, className: propsClassName } = props;

    return (
        <MenuItem as='button'
                  className={classNames("dropdown-footer", propsClassName)}
                  onClick={onClick}
        >
            {children}
        </MenuItem>
    );
};

export default DropdownGroup;
